import React from 'react';
import { useField, useFormikContext } from 'formik';
import DateFnsUtils from '@date-io/date-fns';
import {
    MuiPickersUtilsProvider,
    KeyboardTimePicker,
    KeyboardTimePickerProps,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

interface FormikTimePickerProps extends Omit<KeyboardTimePickerProps, 'value' | 'onChange'> {
    name: string;
    className?: string;
    style?: React.CSSProperties;
    showErrorBelow?: boolean;
}

function numberToDate(value: number | null | undefined): Date | null {
    if (value === null || value === undefined) return null;
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 100);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
}

function dateToNumber(date: MaterialUiPickersDate): number | null {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    return date.getHours() + date.getMinutes() / 100;
}

const FormikTimePicker: React.FC<FormikTimePickerProps> = ({
    name,
    className,
    style,
    required,
    inputVariant = 'filled',
    showErrorBelow,
    ...otherProps
}) => {
    const [field, meta] = useField<number | null>({
        name,
        validate: (value: number | null) => {
            if (required && (value === null || value === undefined)) {
                return 'Required';
            }
            return undefined;
        },
    });
    const { setFieldValue, submitCount } = useFormikContext();

    const showError = Boolean((meta.touched || submitCount > 0) && meta.error);

    const handleDateChange = (date: MaterialUiPickersDate) => {
        setFieldValue(name, dateToNumber(date));
    };

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardTimePicker
                {...otherProps}
                required={required}
                inputVariant={inputVariant}
                ampm={false}
                format="HH:mm"
                mask="__:__"
                placeholder="15:30"
                value={numberToDate(field.value)}
                onChange={handleDateChange}
                onBlur={field.onBlur}
                error={showError}
                helperText={showError ? meta.error : otherProps.helperText}
                FormHelperTextProps={
                    showErrorBelow
                        ? {
                              style: {
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  fontSize: 10,
                                  marginLeft: 0,
                                  ...otherProps.FormHelperTextProps?.style,
                              },
                          }
                        : otherProps.FormHelperTextProps
                }
                invalidDateMessage="Enter complete time (HH:mm)"
                KeyboardButtonProps={{
                    style: { display: 'none' },
                }}
                InputProps={{
                    onClick: (e) => e.preventDefault(),
                }}
                className={className}
                style={{
                    ...style,
                    width: '100px',
                    minWidth: '90px',
                }}
            />
        </MuiPickersUtilsProvider>
    );
};

export default FormikTimePicker;
